import React, { useState, useEffect } from "react";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/Button";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import {
  fetchCategoryFormSettings,
  updateCategoryFormSettings,
  createCategoryFormSettings,
  fetchLatestCategoryFormSettings,
} from "../../../redux/Actions/CategoryFormSettingsAction";
import { CategoryFormSettings } from "../../../modules/shared/model";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { AlertCircle, CheckCircle } from "lucide-react";

const SettingsTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const { settings, setting, loading, error } = useAppSelector((state) => state.categoryFormSettings);

  const [formData, setFormData] = useState<CategoryFormSettings>({
    emergencyCommissionPrice: null,
    emergencyQuotePrice: null,
    emergencyRequestPrice: null,
    standardCommissionPrice: null,
    standardQuotePrice: null,
    canCreateCategory: false,
    isAutoSync: false,
    isServiceDescription: false,
    isServiceLocation: false,
    isContactInformation: false,
    // New detailed pricing fields
    NightHolidayPrice: null,
    NightWeekendPrice: null,
    NightWeekdayPrice: null,
    EveningHolidayPrice: null,
    EveningWeekendPrice: null,
    EveningWeekdayPrice: null,
    DayHolidayPrice: null,
    DayWeekendPrice: null,
    DayWeekdayPrice: null,
    // Revenue and limits (min to max order)
    minRevenueLimit: null,
    maxRevenueLimit: null,
    minPurchaseQuoteLimit: null,
    maxPurchaseQuoteLimit: null,
    contractResponseTime: null,
    contractedTime: null,
    // Tax and fees
    CustomerTax: null,
    HiringTax: null,
    platformFeeQuote: null,
    platformFee: null,
    smartTrackerFee: null,
    webWorkTrackerFee: null,
    // Validity periods
    contractDeleteValidity: null,
    professionalProfileDeleteValidity: null,
    customerProfileDeleteValidity: null,
    quoteDeleteValidity: null,
    jobDeteleValidity: null,
    jobShowValidity: null,
    maxWhatsappDeleteValidity: null,
  });

  const [isDirty, setIsDirty] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Load settings on component mount
  useEffect(() => {
    const fetchSettingsData = async () => {
      await Promise.all([
        dispatch(fetchCategoryFormSettings()),
        dispatch(fetchLatestCategoryFormSettings()),
      ]);
    };

    fetchSettingsData();
  }, [dispatch]);

  const resolveSettingsData = (latestSettings: CategoryFormSettings | null, fallbackSettings: CategoryFormSettings | CategoryFormSettings[] | null) => {
    if (latestSettings) {
      const maybeData = (latestSettings as unknown as { data?: CategoryFormSettings }).data;
      return (maybeData || latestSettings) as CategoryFormSettings;
    }

    if (fallbackSettings) {
      if (Array.isArray(fallbackSettings) && fallbackSettings.length > 0) {
        return fallbackSettings[0];
      }

      const maybeData = (fallbackSettings as unknown as { data?: CategoryFormSettings }).data;
      return (maybeData || fallbackSettings) as CategoryFormSettings;
    }

    return null;
  };

  // Update form data when settings are loaded
  useEffect(() => {
    const resolvedSettings = resolveSettingsData(setting, settings);
    if (resolvedSettings) {
      setFormData((prev) => ({
        ...prev,
        ...resolvedSettings,
      }));
    }
    // If no settings or empty array, keep the default unchecked state
  }, [setting, settings]);

  // Handle form field changes
  const handleFieldChange = (field: keyof CategoryFormSettings, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setIsDirty(true);
    setSaveSuccess(false);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (settings && ((Array.isArray(settings) && settings.length > 0) || (!Array.isArray(settings) && settings))) {
        // Update existing settings - need to extract ID
        let id: string | number;
        if (Array.isArray(settings) && settings.length > 0) {
          id = settings[0].id || settings[0]._id;
        } else if (typeof settings === "object" && !Array.isArray(settings)) {
          id = (settings as any).id || (settings as any)._id;
        } else {
          throw new Error("Unable to determine settings ID for update");
        }

        if (!id) {
          throw new Error("Settings ID not found");
        }

        await dispatch(updateCategoryFormSettings({ id, settings: formData })).unwrap();
      } else {
        // Create new settings
        await dispatch(createCategoryFormSettings(formData)).unwrap();
      }

      await dispatch(fetchLatestCategoryFormSettings());

      setIsDirty(false);
      setSaveSuccess(true);

      // Hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Failed to save settings:", error);
    }
  };

  // Handle form reset
  const handleReset = () => {
    const resolvedSettings = resolveSettingsData(setting, settings);
    if (resolvedSettings) {
      setFormData(resolvedSettings);
    } else {
      // Reset to default unchecked state
      setFormData({
        emergencyCommissionPrice: null,
        emergencyQuotePrice: null,
        emergencyRequestPrice: null,
        standardCommissionPrice: null,
        standardQuotePrice: null,
        canCreateCategory: false,
        isAutoSync: false,
        isServiceDescription: false,
        isServiceLocation: false,
        isContactInformation: false,
        // New detailed pricing fields
        NightHolidayPrice: null,
        NightWeekendPrice: null,
        NightWeekdayPrice: null,
        EveningHolidayPrice: null,
        EveningWeekendPrice: null,
        EveningWeekdayPrice: null,
        DayHolidayPrice: null,
        DayWeekendPrice: null,
        DayWeekdayPrice: null,
        // Revenue and limits (min to max order)
        minRevenueLimit: null,
        maxRevenueLimit: null,
        minPurchaseQuoteLimit: null,
        maxPurchaseQuoteLimit: null,
        contractResponseTime: null,
        contractedTime: null,
        // Tax and fees
        CustomerTax: null,
        HiringTax: null,
        platformFeeQuote: null,
        platformFee: null,
        smartTrackerFee: null,
        webWorkTrackerFee: null,
        // Validity periods
        contractDeleteValidity: null,
        professionalProfileDeleteValidity: null,
        customerProfileDeleteValidity: null,
        quoteDeleteValidity: null,
        jobDeteleValidity: null,
        jobShowValidity: null,
        maxWhatsappDeleteValidity: null,
      });
    }
    setIsDirty(false);
    setSaveSuccess(false);
  };

  return (
    <Card className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div>
        <h3 className="text-lg sm:text-xl font-semibold">Form Settings</h3>
        <p className="text-xs sm:text-sm text-gray-600 mt-1">Configure global form settings and defaults</p>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Success Alert */}
      {saveSuccess && (
        <Alert className="border-green-200 bg-green-50 text-green-800">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>Settings saved successfully!</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700">Default Required Fields</label>
          <div className="mt-2 space-y-2">
            <label className="flex items-center gap-2 text-xs sm:text-sm">
              <input
                type="checkbox"
                checked={formData.isContactInformation}
                onChange={(e) => handleFieldChange("isContactInformation", e.target.checked)}
                className="rounded"
              />
              <span>Contact Information</span>
            </label>
            <label className="flex items-center gap-2 text-xs sm:text-sm">
              <input
                type="checkbox"
                checked={formData.isServiceLocation}
                onChange={(e) => handleFieldChange("isServiceLocation", e.target.checked)}
                className="rounded"
              />
              <span>Service Location</span>
            </label>
            <label className="flex items-center gap-2 text-xs sm:text-sm">
              <input
                type="checkbox"
                checked={formData.isServiceDescription}
                onChange={(e) => handleFieldChange("isServiceDescription", e.target.checked)}
                className="rounded"
              />
              <span>Service Description</span>
            </label>
          </div>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700">Auto-sync Settings</label>
          <div className="mt-2 space-y-2">
            <label className="flex items-center gap-2 text-xs sm:text-sm">
              <input
                type="checkbox"
                checked={formData.isAutoSync}
                onChange={(e) => handleFieldChange("isAutoSync", e.target.checked)}
                className="rounded"
              />
              <span>Automatically sync new categories</span>
            </label>
            <label className="flex items-center gap-2 text-xs sm:text-sm">
              <input
                type="checkbox"
                checked={formData.canCreateCategory}
                onChange={(e) => handleFieldChange("canCreateCategory", e.target.checked)}
                className="rounded"
              />
              <span>Allow category creation</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700">Default Quote Price (Standard)</label>
            <div className="mt-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                placeholder="Enter Standard Quote Price"
                className="w-full pl-6 pr-3 py-1 sm:pl-7 sm:pr-4 sm:py-2 border rounded-lg text-sm"
                value={formData.standardQuotePrice}
                onChange={(e) => handleFieldChange("standardQuotePrice", parseFloat(e.target.value) || 0)}
                min="0"
                step="0.01"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700">Default Quote Price (Emergency)</label>
            <div className="mt-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                placeholder="Enter Emergency Quote Price"
                className="w-full pl-6 pr-3 py-1 sm:pl-7 sm:pr-4 sm:py-2 border rounded-lg text-sm"
                value={formData.emergencyQuotePrice}
                onChange={(e) => handleFieldChange("emergencyQuotePrice", parseFloat(e.target.value) || 0)}
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700">Default Commission (Standard)</label>
            <div className="mt-1 relative">
              <input
                type="number"
                placeholder="Enter Standard Commission"
                className="w-full pr-6 pl-3 py-1 sm:pr-8 sm:pl-4 sm:py-2 border rounded-lg text-sm"
                value={formData.standardCommissionPrice}
                onChange={(e) => handleFieldChange("standardCommissionPrice", parseFloat(e.target.value) || 0)}
                min="0"
                max="100"
                step="0.01"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
            </div>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700">Default Commission (Emergency)</label>
            <div className="mt-1 relative">
              <input
                type="number"
                placeholder="Enter Emergency Commission"
                className="w-full pr-6 pl-3 py-1 sm:pr-8 sm:pl-4 sm:py-2 border rounded-lg text-sm"
                value={formData.emergencyCommissionPrice}
                onChange={(e) => handleFieldChange("emergencyCommissionPrice", parseFloat(e.target.value) || 0)}
                min="0"
                max="100"
                step="0.01"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
            </div>
          </div>
        </div>

        {/* Detailed Emergency Pricing */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-3">Detailed Emergency Pricing</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            {/* Day Pricing */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-600">Day (6 AM - 6 PM)</h4>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Weekday</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    className="w-full pl-6 pr-3 py-1 sm:pl-7 sm:pr-4 sm:py-2 border rounded-lg text-sm"
                    value={formData.DayWeekdayPrice}
                    onChange={(e) => handleFieldChange("DayWeekdayPrice", parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Weekend</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    className="w-full pl-6 pr-3 py-1 sm:pl-7 sm:pr-4 sm:py-2 border rounded-lg text-sm"
                    value={formData.DayWeekendPrice}
                    onChange={(e) => handleFieldChange("DayWeekendPrice", parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Holiday</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    className="w-full pl-6 pr-3 py-1 sm:pl-7 sm:pr-4 sm:py-2 border rounded-lg text-sm"
                    value={formData.DayHolidayPrice}
                    onChange={(e) => handleFieldChange("DayHolidayPrice", parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            {/* Evening Pricing */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-600">Evening (6 PM - 10 PM)</h4>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Weekday</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    className="w-full pl-6 pr-3 py-1 sm:pl-7 sm:pr-4 sm:py-2 border rounded-lg text-sm"
                    value={formData.EveningWeekdayPrice}
                    onChange={(e) => handleFieldChange("EveningWeekdayPrice", parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Weekend</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    className="w-full pl-6 pr-3 py-1 sm:pl-7 sm:pr-4 sm:py-2 border rounded-lg text-sm"
                    value={formData.EveningWeekendPrice}
                    onChange={(e) => handleFieldChange("EveningWeekendPrice", parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Holiday</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    className="w-full pl-6 pr-3 py-1 sm:pl-7 sm:pr-4 sm:py-2 border rounded-lg text-sm"
                    value={formData.EveningHolidayPrice}
                    onChange={(e) => handleFieldChange("EveningHolidayPrice", parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            {/* Night Pricing */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-600">Night (10 PM - 6 AM)</h4>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Weekday</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    className="w-full pl-6 pr-3 py-1 sm:pl-7 sm:pr-4 sm:py-2 border rounded-lg text-sm"
                    value={formData.NightWeekdayPrice}
                    onChange={(e) => handleFieldChange("NightWeekdayPrice", parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Weekend</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    className="w-full pl-6 pr-3 py-1 sm:pl-7 sm:pr-4 sm:py-2 border rounded-lg text-sm"
                    value={formData.NightWeekendPrice}
                    onChange={(e) => handleFieldChange("NightWeekendPrice", parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Holiday</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    className="w-full pl-6 pr-3 py-1 sm:pl-7 sm:pr-4 sm:py-2 border rounded-lg text-sm"
                    value={formData.NightHolidayPrice}
                    onChange={(e) => handleFieldChange("NightHolidayPrice", parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Revenue and Limits */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-3">Revenue and Limits</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Min Revenue Limit</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  placeholder="0.00"
                  className="w-full pl-6 pr-3 py-1 sm:pl-7 sm:pr-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.minRevenueLimit || ""}
                  onChange={(e) => handleFieldChange("minRevenueLimit", parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Max Revenue Limit</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  placeholder="0.00"
                  className="w-full pl-6 pr-3 py-1 sm:pl-7 sm:pr-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.maxRevenueLimit || ""}
                  onChange={(e) => handleFieldChange("maxRevenueLimit", parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Min Purchase Quote Limit</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  placeholder="0.00"
                  className="w-full pl-6 pr-3 py-1 sm:pl-7 sm:pr-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.minPurchaseQuoteLimit || ""}
                  onChange={(e) => handleFieldChange("minPurchaseQuoteLimit", parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Max Purchase Quote Limit</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  placeholder="0.00"
                  className="w-full pl-6 pr-3 py-1 sm:pl-7 sm:pr-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.maxPurchaseQuoteLimit || ""}
                  onChange={(e) => handleFieldChange("maxPurchaseQuoteLimit", parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Contract Response Time (hours)</label>
              <div className="relative">
                <input
                  type="number"
                  placeholder="0"
                  className="w-full pr-6 pl-3 py-1 sm:pr-8 sm:pl-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.contractResponseTime || ""}
                  onChange={(e) => handleFieldChange("contractResponseTime", parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.1"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">hrs</span>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Contracted Time (days)</label>
              <div className="relative">
                <input
                  type="number"
                  placeholder="0"
                  className="w-full pr-6 pl-3 py-1 sm:pr-8 sm:pl-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.contractedTime || ""}
                  onChange={(e) => handleFieldChange("contractedTime", parseFloat(e.target.value) || 0)}
                  min="0"
                  step="1"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">days</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tax and Fees */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-3">Tax and Fees</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Customer Tax</label>
              <div className="relative">
                <input
                  type="number"
                  placeholder="0.00"
                  className="w-full pr-6 pl-3 py-1 sm:pr-8 sm:pl-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.CustomerTax || ""}
                  onChange={(e) => handleFieldChange("CustomerTax", parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  step="0.01"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Hiring Tax</label>
              <div className="relative">
                <input
                  type="number"
                  placeholder="0.00"
                  className="w-full pr-6 pl-3 py-1 sm:pr-8 sm:pl-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.HiringTax || ""}
                  onChange={(e) => handleFieldChange("HiringTax", parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  step="0.01"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Platform Fee Quote</label>
              <div className="relative">
                <input
                  type="number"
                  placeholder="0.00"
                  className="w-full pr-6 pl-3 py-1 sm:pr-8 sm:pl-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.platformFeeQuote || ""}
                  onChange={(e) => handleFieldChange("platformFeeQuote", parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  step="0.01"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Platform Fee</label>
              <div className="relative">
                <input
                  type="number"
                  placeholder="0.00"
                  className="w-full pr-6 pl-3 py-1 sm:pr-8 sm:pl-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.platformFee || ""}
                  onChange={(e) => handleFieldChange("platformFee", parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  step="0.01"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Smart Tracker Fee</label>
              <div className="relative">
                <input
                  type="number"
                  placeholder="0.00"
                  className="w-full pr-6 pl-3 py-1 sm:pr-8 sm:pl-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.smartTrackerFee || ""}
                  onChange={(e) => handleFieldChange("smartTrackerFee", parseFloat(e.target.value) || 0)}
                  min="0"
                  step="1"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Web Work Tracker Fee</label>
              <div className="relative">
                <input
                  type="number"
                  placeholder="0.00"
                  className="w-full pr-6 pl-3 py-1 sm:pr-8 sm:pl-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.webWorkTrackerFee || ""}
                  onChange={(e) => handleFieldChange("webWorkTrackerFee", parseFloat(e.target.value) || 0)}
                  min="0"
                  step="1"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Validity Periods */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-3">Validity Periods (days)</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Contract Delete Validity</label>
              <div className="relative">
                <input
                  type="number"
                  placeholder="0"
                  className="w-full pr-6 pl-3 py-1 sm:pr-8 sm:pl-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.contractDeleteValidity || ""}
                  onChange={(e) => handleFieldChange("contractDeleteValidity", parseFloat(e.target.value) || 0)}
                  min="0"
                  step="1"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">days</span>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Professional Profile Delete Validity</label>
              <div className="relative">
                <input
                  type="number"
                  placeholder="0"
                  className="w-full pr-6 pl-3 py-1 sm:pr-8 sm:pl-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.professionalProfileDeleteValidity || ""}
                  onChange={(e) => handleFieldChange("professionalProfileDeleteValidity", parseFloat(e.target.value) || 0)}
                  min="0"
                  step="1"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">days</span>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Customer Profile Delete Validity</label>
              <div className="relative">
                <input
                  type="number"
                  placeholder="0"
                  className="w-full pr-6 pl-3 py-1 sm:pr-8 sm:pl-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.customerProfileDeleteValidity || ""}
                  onChange={(e) => handleFieldChange("customerProfileDeleteValidity", parseFloat(e.target.value) || 0)}
                  min="0"
                  step="1"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">days</span>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Quote Delete Validity</label>
              <div className="relative">
                <input
                  type="number"
                  placeholder="0"
                  className="w-full pr-6 pl-3 py-1 sm:pr-8 sm:pl-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.quoteDeleteValidity || ""}
                  onChange={(e) => handleFieldChange("quoteDeleteValidity", parseFloat(e.target.value) || 0)}
                  min="0"
                  step="1"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">days</span>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Job Delete Validity</label>
              <div className="relative">
                <input
                  type="number"
                  placeholder="0"
                  className="w-full pr-6 pl-3 py-1 sm:pr-8 sm:pl-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.jobDeteleValidity || ""}
                  onChange={(e) => handleFieldChange("jobDeteleValidity", parseFloat(e.target.value) || 0)}
                  min="0"
                  step="1"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">days</span>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Job Show Validity</label>
              <div className="relative">
                <input
                  type="number"
                  placeholder="0"
                  className="w-full pr-6 pl-3 py-1 sm:pr-8 sm:pl-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.jobShowValidity || ""}
                  onChange={(e) => handleFieldChange("jobShowValidity", parseFloat(e.target.value) || 0)}
                  min="0"
                  step="1"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">days</span>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Maximum Whatsapp Delete Validity</label>
              <div className="relative">
                <input
                  type="number"
                  placeholder="0"
                  className="w-full pr-6 pl-3 py-1 sm:pr-8 sm:pl-4 sm:py-2 border rounded-lg text-sm"
                  value={formData.maxWhatsappDeleteValidity || ""}
                  onChange={(e) => handleFieldChange("maxWhatsappDeleteValidity", parseFloat(e.target.value) || 0)}
                  min="0"
                  step="1"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">days</span>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-4 sm:pt-6 border-t flex flex-col sm:flex-row justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            disabled={loading || !isDirty}
            className="w-full sm:w-auto text-sm"
          >
            Reset
          </Button>
          <Button
            type="submit"
            disabled={loading || !isDirty}
            className="w-full sm:w-auto text-sm"
          >
            {loading ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default SettingsTab;
